/**
 * Утилиты для работы с рабочим расписанием филиалов
 */

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

const JS_DAY_TO_DB_KEY: Record<number, typeof WEEK_DAYS[number]> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  0: 'sunday'
};

const DB_KEY_TO_RUSSIAN_DAY: Record<typeof WEEK_DAYS[number], string> = {
  monday: 'понедельник',
  tuesday: 'вторник',
  wednesday: 'среду',
  thursday: 'четверг',
  friday: 'пятницу',
  saturday: 'субботу',
  sunday: 'воскресенье'
};

interface DaySchedule {
  startTime: string | null;
  endTime: string | null;
  isWorking: boolean;
}

/**
 * Получает расписание филиала на конкретный день недели
 */
export const getDaySchedule = (
  workSchedule: any,
  dayKey: typeof WEEK_DAYS[number]
): DaySchedule => {
  const day = workSchedule?.[dayKey];

  if (!day) {
    return { startTime: null, endTime: null, isWorking: false };
  }

  return {
    startTime: day.startTime,
    endTime: day.endTime,
    isWorking: day.isWorking || false
  };
};

/**
 * Находит ближайший рабочий день и его рабочее время
 */
export const getNearestWorkingHours = (workSchedule: any): {
  startTime: string;
  endTime: string;
  dayName: string;
  daysAhead: number;
} => {
  let daysAhead = 0;
  let currentDate = new Date();

  while (daysAhead < 7) {
    const currentDayKey = JS_DAY_TO_DB_KEY[currentDate.getDay()];
    const schedule = getDaySchedule(workSchedule, currentDayKey);

    if (schedule.isWorking && schedule.startTime && schedule.endTime) {
      return {
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        dayName: daysAhead === 0 ? 'сегодня' : DB_KEY_TO_RUSSIAN_DAY[currentDayKey],
        daysAhead
      };
    }

    daysAhead++;
    currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + daysAhead);
  }

  return {
    startTime: '09:00',
    endTime: '18:00',
    dayName: 'ближайший рабочий день',
    daysAhead: 0
  };
};

/**
 * Проверяет, является ли текущий момент рабочим временем
 */
export const isCurrentlyWorkingHours = (workSchedule: any): boolean => {
  const { startTime, endTime, daysAhead } = getNearestWorkingHours(workSchedule);

  if (daysAhead !== 0) return false;

  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return currentTime >= startTime && currentTime <= endTime;
};
